import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-hot-toast';
import { useSite } from '../../context/SiteContext';
import { getBusinessDateISO } from '../../utils/businessDate';
import { addNovolexLogo } from '../../utils/pdfBranding';
import AdditionalConversionPdfReport from './AdditionalConversionPdfReport';
import ConversionPoundsPdfReport from './ConversionPoundsPdfReport';
import ConversionLaborAxPdfReport from './ConversionLaborAxPdfReport';

const formatQuantity = (value) => Number(value || 0).toLocaleString('en-US');
export const formatDailyQuantity = (value, hasOpenReports = false) => (
  `${formatQuantity(value)}${hasOpenReports ? ' !' : ''}`
);

const styleOpenReportQuantityCell = (cellData) => {
  if (cellData.section !== 'body') return;
  const containsWarning = (cellData.cell.text || []).some((text) => String(text).endsWith(' !'));
  if (containsWarning) {
    cellData.cell.styles.textColor = [180, 83, 9];
    cellData.cell.styles.fontStyle = 'bold';
  }
};

const drawRecordStar = (doc, centerX, centerY) => {
  const points = Array.from({ length: 10 }, (_, index) => {
    const angle = -Math.PI / 2 + index * Math.PI / 5;
    const radius = index % 2 === 0 ? 6 : 2.8;
    return [centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius];
  });

  doc.setFillColor(245, 158, 11);
  doc.lines(
    points.slice(1).map((point, index) => [point[0] - points[index][0], point[1] - points[index][1]]),
    points[0][0],
    points[0][1],
    [1, 1],
    'F',
    true
  );
};

export const getDailyGoodQuantitySummarySections = (report) => {
  const summaries = report.good_quantity_summaries || {};
  const section = (title, column, key) => ({
    title,
    column,
    rows: Array.isArray(summaries[key]) ? summaries[key] : [],
  });

  return [
    section('Por Work Order', 'Work Order', 'work_order'),
    section('Por Bag Size', 'Bag Size', 'bag_size'),
    section('Por Bag Style', 'Bag Style', 'bag_style'),
    section('Por Packing', 'Packing', 'packing'),
  ];
};

export const buildDailyProductionPdf = (report) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const activeShifts = report.shifts.filter((shift) => (
    Number(report.shift_totals?.[String(shift.id)] || 0) !== 0
    || Number(report.shift_scrap_totals?.[String(shift.id)] || 0) !== 0
  ));
  const shifts = activeShifts.length === 2 ? activeShifts : report.shifts;
  const shiftHeaders = shifts.map((shift) => shift.code || shift.name);
  const scrapHeaders = shiftHeaders.map((label) => `Scrap ${label}`);
  const tableWidth = pageWidth - margin * 2;
  const quantityColumnWidth = tableWidth * (shifts.length > 2 ? 0.52 : 0.40) / (shifts.length * 2 + 2.6);
  const itemColumnStyles = {
    0: { cellWidth: tableWidth * 0.08, halign: 'left', fontStyle: 'bold' },
    1: { cellWidth: tableWidth * (shifts.length > 2 ? 0.16 : 0.20), halign: 'left' },
    2: { cellWidth: tableWidth * (shifts.length > 2 ? 0.24 : 0.32), halign: 'left' },
    ...Object.fromEntries(Array.from({ length: shifts.length * 2 + 2 }, (_, index) => (
      [index + 3, { cellWidth: quantityColumnWidth }]
    ))),
    [shifts.length + 3]: { cellWidth: quantityColumnWidth * 1.3 },
    [shifts.length * 2 + 4]: { cellWidth: quantityColumnWidth * 1.3 },
  };
  const shiftValues = (totals, openFlags) => shifts.map((shift) => (
    formatDailyQuantity(
      totals?.[String(shift.id)],
      Boolean(openFlags?.[String(shift.id)])
    )
  ));
  const scrapTotal = (aggregate) => formatDailyQuantity(
    Object.values(aggregate.shift_scrap_totals || {}).reduce((sum, value) => sum + Number(value || 0), 0),
    aggregate.has_open_reports
  );

  doc.setProperties({
    title: `Producción diaria ${report.date}`,
    subject: 'Producción de Conversión agrupada por Máquina e ItemID',
  });

  addNovolexLogo(doc, { x: margin, y: 14, width: 82 });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(30, 64, 175);
  doc.text('Reporte diario de producción', margin + 100, 42);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(51, 65, 85);
  doc.text(`${report.site?.code || ''}${report.site?.name ? ` - ${report.site.name}` : ''}`, margin, 62);
  doc.text(`Fecha: ${report.date}`, margin, 78);
  doc.text(report.include_open ? 'Incluye reportes abiertos y cerrados' : 'Solo reportes cerrados', margin, 94);

  doc.setFont('helvetica', 'bold');
  const totalLabel = `Total realizado: ${formatDailyQuantity(report.total, report.has_open_reports)}`;
  doc.text(
    totalLabel,
    pageWidth - margin,
    78,
    { align: 'right' }
  );
  if (report.is_all_time_record) {
    drawRecordStar(doc, pageWidth - margin - doc.getTextWidth(totalLabel) - 11, 74);
  }

  let summaryStartY = 112;
  if (report.has_open_reports) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(180, 83, 9);
    doc.text('! Cantidad que incluye al menos un reporte abierto', margin, 108);
    summaryStartY = 120;
  }

  const summaryRows = report.machines.map((machine) => [
    machine.code,
    ...shiftValues(machine.shift_totals, machine.shift_has_open_reports),
    formatDailyQuantity(machine.total, machine.has_open_reports),
    ...shiftValues(machine.shift_scrap_totals, machine.shift_has_open_reports),
    scrapTotal(machine),
  ]);
  summaryRows.push([
    'TOTAL GENERAL',
    ...shiftValues(report.shift_totals, report.shift_has_open_reports),
    formatDailyQuantity(report.total, report.has_open_reports),
    ...shiftValues(report.shift_scrap_totals, report.shift_has_open_reports),
    scrapTotal(report),
  ]);

  autoTable(doc, {
    startY: summaryStartY,
    head: [['Máquina', ...shiftHeaders, 'Total', ...scrapHeaders, 'Total Scrap']],
    body: summaryRows,
    theme: 'grid',
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 10, halign: 'right' },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
    didParseCell: (cellData) => {
      styleOpenReportQuantityCell(cellData);
      if (cellData.section === 'body' && cellData.row.index === summaryRows.length - 1) {
        cellData.cell.styles.fillColor = [219, 234, 254];
        cellData.cell.styles.fontStyle = 'bold';
      }
    },
  });

  if (report.machines.length > 0) doc.addPage();
  let y = margin;

  report.machines.forEach((machine, machineIndex) => {
    if (y > pageHeight - 120) {
      doc.addPage();
      y = margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 64, 175);
    doc.text(`Máquina ${machine.code}${machine.name ? ` - ${machine.name}` : ''}`, margin, y);
    y += 10;

    const itemRows = machine.items.length > 0
      ? machine.items.map((item) => [
        item.item_id,
        (item.work_orders || []).join(', ') || '—',
        item.item_name || '',
        ...shiftValues(item.shift_totals, item.shift_has_open_reports),
        formatDailyQuantity(item.total, item.has_open_reports),
        ...shiftValues(item.shift_scrap_totals, item.shift_has_open_reports),
        scrapTotal(item),
      ])
      : [['Sin producción', '', '', ...shifts.map(() => '0'), '0', ...shifts.map(() => '0'), '0']];

    itemRows.push([
      `TOTAL ${machine.code}`,
      '',
      '',
      ...shiftValues(machine.shift_totals, machine.shift_has_open_reports),
      formatDailyQuantity(machine.total, machine.has_open_reports),
      ...shiftValues(machine.shift_scrap_totals, machine.shift_has_open_reports),
      scrapTotal(machine),
    ]);

    autoTable(doc, {
      startY: y,
      head: [['ItemID', 'Work Order', 'Nombre de la bolsa', ...shiftHeaders, 'Total', ...scrapHeaders, 'Total Scrap']],
      body: itemRows,
      theme: 'grid',
      margin: { left: margin, right: margin, top: margin },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold', halign: 'center' },
      bodyStyles: { fontSize: 10, halign: 'right' },
      columnStyles: itemColumnStyles,
      didParseCell: (cellData) => {
        styleOpenReportQuantityCell(cellData);
        if (cellData.section === 'body' && cellData.row.index === itemRows.length - 1) {
          cellData.cell.styles.fillColor = [219, 234, 254];
          cellData.cell.styles.fontStyle = 'bold';
        }
      },
    });

    y = (doc.lastAutoTable?.finalY || y) + 22;
    if (machineIndex < report.machines.length - 1 && y > pageHeight - 120) {
      doc.addPage();
      y = margin;
    }
  });

  doc.addPage();
  y = margin;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(30, 64, 175);
  doc.text('Resumen de cantidades buenas', margin, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text('Los valores corresponden exclusivamente a bolsas buenas.', margin, y);
  y += 18;

  getDailyGoodQuantitySummarySections(report).forEach((section) => {
    if (y > pageHeight - 100) {
      doc.addPage();
      y = margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(30, 64, 175);
    doc.text(section.title, margin, y);

    const rows = section.rows.map((row) => [
      row.value,
      formatDailyQuantity(row.bags_good, row.has_open_reports),
    ]);
    rows.push(['TOTAL', formatDailyQuantity(report.total, report.has_open_reports)]);

    autoTable(doc, {
      startY: y + 7,
      head: [[section.column, 'Bolsas buenas']],
      body: rows,
      theme: 'grid',
      tableWidth: Math.min(620, pageWidth - (margin * 2)),
      margin: { left: margin, right: margin, top: margin },
      headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold' },
      bodyStyles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'right', cellWidth: 140 } },
      didParseCell: (cellData) => {
        styleOpenReportQuantityCell(cellData);
        if (cellData.section === 'body' && cellData.row.index === rows.length - 1) {
          cellData.cell.styles.fillColor = [219, 234, 254];
          cellData.cell.styles.fontStyle = 'bold';
        }
      },
    });

    y = (doc.lastAutoTable?.finalY || y) + 22;
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Página ${page} de ${pageCount}`, pageWidth - margin, pageHeight - 18, { align: 'right' });
  }

  return doc;
};

const DailyProductionPdfReport = ({ onBack }) => {
  const { currentSite } = useSite();
  const previewRef = useRef(null);
  const [date, setDate] = useState(getBusinessDateISO());
  const [includeOpen, setIncludeOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const clearPreview = () => {
    setReport(null);
    setPdfUrl(null);
  };

  const generatePreview = async () => {
    if (!currentSite?.id) {
      toast.error('Selecciona un sitio');
      return;
    }
    if (!date) {
      toast.error('Selecciona una fecha');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get('/conversion/reportes-pdf/produccion-diaria', {
        params: {
          date,
          site_id: currentSite.id,
          include_open: includeOpen ? 1 : 0,
        },
      });
      const doc = buildDailyProductionPdf(data);
      const nextUrl = URL.createObjectURL(doc.output('blob'));
      setReport(data);
      setPdfUrl(nextUrl);
    } catch (error) {
      const detail = error.response?.data?.message
        || error.response?.data?.errors?.date?.[0]
        || 'No fue posible generar el reporte';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const printPdf = () => {
    const frameWindow = previewRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.print();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
      <div>
        <button type="button" onClick={onBack} className="mb-2 text-sm font-bold text-blue-700 hover:text-blue-900">← Todos los reportes</button>
        <h1 className="text-3xl font-extrabold text-blue-900">Reporte Diario de Producción</h1>
        <p className="mt-1 text-slate-600">Producción por Máquina, ItemID y turno.</p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[220px]">
            <label htmlFor="pdf-report-date" className="mb-1 block text-sm font-bold text-slate-700">Fecha</label>
            <input
              id="pdf-report-date"
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
                clearPreview();
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={includeOpen}
              onChange={(event) => {
                setIncludeOpen(event.target.checked);
                clearPreview();
              }}
              className="h-4 w-4"
            />
            Incluir reportes abiertos
          </label>

          <button
            type="button"
            onClick={generatePreview}
            disabled={loading}
            className="rounded-lg bg-blue-700 px-5 py-2 font-bold text-white shadow hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {loading ? 'Generando…' : 'Generar vista previa'}
          </button>

          {pdfUrl && (
            <>
              <button
                type="button"
                onClick={printPdf}
                className="rounded-lg border border-blue-300 bg-white px-5 py-2 font-bold text-blue-800 hover:bg-blue-50"
              >
                Imprimir
              </button>
              <a
                href={pdfUrl}
                download={`produccion-diaria-${date}.pdf`}
                className="rounded-lg border border-slate-300 bg-white px-5 py-2 font-bold text-slate-800 no-underline hover:bg-slate-50"
              >
                Descargar PDF
              </a>
            </>
          )}
        </div>
      </section>

      {report && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-blue-700">Máquinas</div>
            <div className="mt-1 text-2xl font-extrabold text-blue-950">{report.machines.length}</div>
          </div>
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-blue-700">Turnos</div>
            <div className="mt-1 text-2xl font-extrabold text-blue-950">{report.shifts.length}</div>
          </div>
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-blue-700">Cantidad realizada</div>
            <div className="mt-1 flex items-center gap-2 text-2xl font-extrabold text-blue-950">
              {formatQuantity(report.total)}
              {report.is_all_time_record && (
                <span className="text-amber-500" title="Récord histórico de producción" aria-label="Récord histórico de producción">★</span>
              )}
            </div>
          </div>
        </div>
      )}

      {pdfUrl ? (
        <section className="overflow-hidden rounded-2xl border border-slate-300 bg-slate-100 shadow-sm">
          <iframe
            ref={previewRef}
            src={pdfUrl}
            title="Vista previa del reporte diario de producción"
            className="h-[75vh] min-h-[620px] w-full"
          />
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-slate-500">
          Selecciona una fecha y genera la vista previa del reporte diario.
        </section>
      )}
    </div>
  );
};

const REPORTS = [
  { key: 'daily', title: 'Reporte Diario de Producción', description: 'Máquinas, ItemID, turnos y cantidades realizadas.' },
  { key: 'labor_ax', title: 'Mano de Obra AX', description: 'Renglones A/X por máquina para la fecha y el turno seleccionados.' },
  { key: 'pounds', title: 'Reporte de libras', description: 'Libras estimadas por máquina y total para un rango de fechas.' },
  { key: 'work_orders', title: 'Producción por Work Order', description: 'Bolsas buenas, máquinas, tarimas, rollos y Serie de Papel.' },
  { key: 'perlas', title: "Perla's Report", description: 'PDF vertical de Bolsa Buena por máquina, Work Order e ItemID, con máquinas separadas en orden ascendente.' },
  { key: 'roll_trace', title: 'Trazabilidad por rollo y serie', description: 'Producción y Scrap por Work Order, rollo y Serie de Papel, con detalle por renglón.' },
  { key: 'weekly', title: 'Resumen semanal de producción', description: 'Producción, Scrap, tiempos y gráficas por día y máquina.' },
  { key: 'scrap', title: 'Reporte de Scrap', description: 'Detalle y ranking de Scrap por máquina, Work Order e Item.' },
  { key: 'monthly_scrap', title: 'Resumen mensual de Scrap', description: 'Indicadores y gráficas de Scrap por máquina, día y turno, con rango de fechas editable.' },
  { key: 'downtime', title: 'Reporte de tiempos muertos', description: 'Códigos de paro, frecuencia y minutos acumulados.' },
  { key: 'items', title: 'Reporte por Item', description: 'Características, producción, Scrap, BPM y máquinas.' },
  { key: 'machines', title: 'Desempeño por máquina', description: 'Producción, calidad, disponibilidad, BPM y tiempos.' },
  { key: 'downtime_codes', title: 'Tiempos por código de tiempo muerto', description: 'Minutos por código, frecuencia y Pareto acumulado.' },
  { key: 'roll_changes', title: 'Cambios de rollo', description: 'Eventos 201 por máquina y turno: minutos, promedio, mediana y detalle.' },
  { key: 'employees', title: 'Reporte de empleados', description: 'Turnos, producción y Scrap asociados por puesto.' },
  { key: 'audit', title: 'Cierre y auditoría', description: 'Creación, cierre, reaperturas, responsables y motivos.' },
];

const ConversionPdfReports = () => {
  const [selectedReport, setSelectedReport] = useState(null);

  if (selectedReport === 'daily') {
    return <DailyProductionPdfReport onBack={() => setSelectedReport(null)} />;
  }

  if (selectedReport === 'pounds') {
    return <ConversionPoundsPdfReport onBack={() => setSelectedReport(null)} />;
  }

  if (selectedReport === 'labor_ax') {
    return <ConversionLaborAxPdfReport onBack={() => setSelectedReport(null)} />;
  }

  if (selectedReport) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <AdditionalConversionPdfReport type={selectedReport} onBack={() => setSelectedReport(null)} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold text-blue-900">Reportes PDF</h1>
        <p className="mt-1 text-slate-600">Selecciona un reporte para generar su vista previa, imprimirlo o descargarlo.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {REPORTS.map((report) => (
          <button
            key={report.key}
            type="button"
            onClick={() => setSelectedReport(report.key)}
            className="group flex flex-col rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"
          >
            <div className="flex items-start gap-2.5">
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 group-hover:bg-blue-700 group-hover:text-white">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 3h7l5 5v13H7a2 2 0 01-2-2V5a2 2 0 012-2zM14 3v6h6M9 13h6M9 17h4" />
              </svg>
            </div>
            <h2 className="m-0 text-sm leading-5 font-extrabold text-slate-900">{report.title}</h2>
            </div>
            <p className="mb-0 mt-2 text-xs leading-5 text-slate-600">{report.description}</p>
            <div className="mt-auto pt-2 text-xs font-bold text-blue-700">Abrir reporte →</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ConversionPdfReports;
